import { api } from '@/lib/api'
import { unmaskMaskedId } from '@/lib/backend-ids'
import { normalizeOrderItemMediaRecord } from '@/lib/order-item-media'
import { mergePlannerResourceLinks, normalizePlannerResourceLinks } from '@/lib/planner-links'
import { API_PATHS } from '@/constants/api-paths'
import { ApiResponse, OrderRecurrenceInfo, PaginatedResponse, StoreOrderDetail, StoreOrderItem, StoreOrderItemMedia, StoreOrderPayment, StoreOrderRefund, StoreOrderSummary, StoreOrderTotalLine } from '@/types'

export type OrdersQueryParams = {
  page?: number
  page_size?: number
  order_id?: string
  service_instance_id?: string | number | null
  segment?: string
  date_from?: string
  date_to?: string
  status?: string
  is_paid?: boolean
  pre_order?: boolean
  user_id?: string | number
  booking_scope?: 'open' | 'completed'
  ordering?: string
}

export type OrdersListResponse = PaginatedResponse<StoreOrderSummary> & {
  order_count_stat?: Array<{
    status?: number | null
    label?: string
    count?: number
  }>
  statistics_data?: {
    unpaid_totals?: {
      total_unpaid_amount?: string | number | null
      unpaid_order_count?: number | null
      currency?: string | null
    }
  }
}

export type OrdersStatsResponse = {
  order_count_stat?: Array<{
    status?: number | null
    label?: string
    count?: number
  }>
  statistics_data?: {
    unpaid_totals?: {
      total_unpaid_amount?: string | number | null
      unpaid_order_count?: number | null
      currency?: string | null
    }
  }
}

type BackendOrderStatusData = {
  label?: string
  key?: string
  status?: string | number
  is_terminal?: boolean | null
  more_info?: unknown
} | null

type BackendOrderSummary = {
  id?: string
  reference_id?: string | number | null
  status?: string | number | BackendOrderStatusData | null
  status_data?: BackendOrderStatusData | Array<Record<string, unknown>>
  notes?: string | null
  type?: string | number | null
  type_str?: string | null
  order_type_str?: string | null
  serve_type?: string | number | null
  serve_type_key?: string | null
  serve_type_label?: string | null
  source_type?: string | number | null
  source_type_str?: string | null
  order_time?: string | null
  delivery_time?: string | null
  created_on?: string | null
  is_scheduled?: boolean | null
  is_paid?: boolean | null
  payment_method?: string | number | null
  payment_status?: string | number | null
  payment_url?: string | null
  total?: string | number | null
  currency?: string | null
  user_data?: {
    id?: string | number | null
    name?: string | null
    email?: string | null
    phone?: string | null
    mobile?: string | null
  } | null
  delivery_address_data?: Record<string, unknown> | null
  address_label?: string | null
  order_item_count?: number | null
  order_items_count?: number | null
  linked_job_count?: number | null
  linked_jobs?: number | null
  job_id?: string | number | null
  job_ref?: string | null
  service_instance_id?: string | null
  job_service_instance_id?: string | null
  jobs_summary?: Array<{
    id?: string | number
    job_ref?: string | null
    title?: string | null
    status?: string | null
    order_item_id?: string | number | null
    scheduled_start?: string | null
    assigned_worker_count?: number
    service_instance_id?: string | null
    service_instance_pk?: string | number | null
  }>
  recurrence?: {
    rule?: string | null
    interval_days?: number | null
    weekdays?: Array<number | string> | null
    month_day?: number | null
    end_date?: string | null
    occurrence_count?: number | null
    payment_plan?: string | null
    per_occurrence_total?: string | null
    is_bounded?: boolean
  } | null
  name?: string | null
}

const resolveTerminalFlag = (statusData: unknown): boolean | null => {
  if (!statusData || typeof statusData !== 'object') {
    return null
  }

  const source = statusData as Record<string, unknown>
  if (typeof source.is_terminal === 'boolean') {
    return source.is_terminal
  }

  const moreInfo = source.more_info
  if (moreInfo && typeof moreInfo === 'object') {
    const nested = moreInfo as Record<string, unknown>
    if (typeof nested.is_terminal === 'boolean') {
      return nested.is_terminal
    }
  }

  return null
}

type BackendOrderDetailItem = {
  included_features?: Array<{
    id?: string | number | null
    name?: string | null
    code?: string | null
    feature_type?: string | null
  }>
  excluded_features?: Array<{
    id?: string | number | null
    name?: string | null
    code?: string | null
    feature_type?: string | null
  }>
  product_data?: {
    name?: string | null
    type?: string | number | null
    planner_resource_links?: unknown[]
  } | null
  product_variant_data?: {
    label?: string | null
    product_variant_name?: string | null
    product_variant_label?: string | null
    planner_resource_links?: unknown[]
  } | null
  order_addon_data?: Array<{
    addon_data?: {
      name?: string | null
      planner_resource_links?: unknown[]
    } | null
    addon_product_variant_data?: {
      planner_resource_links?: unknown[]
    } | null
  }>
  qty?: number | null
  unit_price?: string | number | null
  final_price?: string | number | null
  location_text?: string | null
  latitude?: string | number | null
  longitude?: string | number | null
  job_id?: string | number | null
  job_ref?: string | null
  job_status?: string | null
  job_service_instance_id?: string | null
  has_job?: boolean | null
  assigned_worker_count?: number | null
}

type BackendOrderDetail = BackendOrderSummary & {
  _id?: string | number | null
  service_instance?: string | number | null
  service_instance_id?: string | null
  service_instance_data?: {
    name?: string | null
  } | null
  full_name?: string | null
  email?: string | null
  mobile?: string | null
  payment_status?: string | number | null
  payment_mode_name?: string | null
  payment_url?: string | null
  final_total?: string | number | null
  order_item_data?: BackendOrderDetailItem[]
  order_items_data?: BackendOrderDetailItem[]
  total?: Array<{
    label?: string
    amount?: string | number | null
    currency?: string | null
    sign?: string | null
    value?: string | number | null
  }> | null
  status_data?: Array<Record<string, unknown>> | BackendOrderStatusData
  payment_data?: Array<Record<string, unknown>>
  offers_applied?: Array<Record<string, unknown>>
  planner_resource_links?: unknown[]
  job_id?: string | number | null
  job_ref?: string | null
  has_job?: boolean | null
  linked_job_count?: number | null
  linked_jobs?: number | null
  job_scope?: string | null
  job_ids?: Array<string | number>
  job_service_instance_id?: string | null
  jobs_summary?: Array<{
    id?: string | number
    job_ref?: string | null
    title?: string | null
    status?: string | null
    order_item_id?: string | number | null
    scheduled_start?: string | null
    assigned_worker_count?: number
    service_instance_id?: string | null
    service_instance_pk?: string | number | null
  }>
}

type BackendOrderPayment = {
  id?: string | number | null
  _id?: string | number | null
  amount?: string | number | null
  currency?: string | null
  payment_reference_id?: string | null
  payment_reference_number?: string | null
  order_reference_id?: string | number | null
  customer_name?: string | null
  customer_phone?: string | null
  customer_email?: string | null
  payment_service?: string | number | null
  payment_method?: string | number | null
  payment_mode?: string | number | null
  payment_status?: string | number | null
  created_on?: string | null
  updated_on?: string | null
}

type BackendOrderRefund = {
  id?: string | number | null
  _id?: string | number | null
  order?: string | number | null
  order_id?: string | number | null
  order_reference_id?: string | number | null
  order_total_amount?: string | number | null
  payment_transaction?: string | number | null
  amount?: string | number | null
  gross_amount?: string | number | null
  deduction_amount?: string | number | null
  currency?: string | null
  status?: string | number | null
  source?: string | null
  settlement_type?: string | null
  refund_wallet_id?: string | number | null
  refund_wallet_label?: string | null
  applied_rule_id?: string | number | null
  refund_rule_title?: string | null
  gateway_refund_id?: string | null
  reason?: string | null
  rule_snapshot?: Record<string, unknown> | null
  payment_method_label?: string | null
  payment_reference_id?: string | null
  gateway_payment_reference_id?: string | null
  paid_status?: string | null
  paid_on?: string | null
  auto_refund_eligible?: boolean | null
  auto_refund_reason?: string | null
  initiated_by?: {
    id?: string | number | null
    name?: string | null
  } | null
  reviewed_by?: {
    id?: string | number | null
    name?: string | null
  } | null
  created_on?: string | null
  updated_on?: string | null
}

type BackendOrderItemMedia = {
  id?: string | null
  _id?: string | number | null
  order_item?: string | null
  _order_item?: string | number | null
  basket_item?: string | null
  _basket_item?: string | number | null
  mime?: string | null
  path?: string | null
  file_size?: number | null
  type?: string | number | null
  type_str?: string | null
  created_on?: string | null
  updated_on?: string | null
}

export interface CreateOrderPaymentPayload {
  order: string | number
  amount: string | number
  payment_method: string | number
  payment_mode?: string | number
  payment_service?: string | number
}

export type ResendOrderConfirmationPayload = {
  order: string | number
  email?: string
}

export const resolveOrderPrimaryKey = (order: Pick<StoreOrderDetail, 'raw_id' | '_id' | 'id'>): string | number | null => {
  if (order.raw_id != null && Number.isFinite(order.raw_id) && order.raw_id > 0) {
    return order.raw_id
  }

  const unmasked = unmaskMaskedId(order._id)
  if (unmasked != null && unmasked > 0) {
    return unmasked
  }

  const token = String(order.id ?? '').trim()
  return token.length > 0 ? token : null
}

const toText = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
  }

  if (typeof value === 'number') {
    return String(value)
  }

  return null
}

const getAddressLabel = (address?: Record<string, unknown> | null): string | null => {
  if (!address) {
    return null
  }

  const parts = [
    toText(address.building_name),
    toText(address.address),
    toText(address.landmark),
    toText(address.city),
    toText(address.state),
    toText(address.country),
    toText(address.pincode),
  ].filter(Boolean)

  return parts.length > 0 ? parts.join(', ') : null
}

const normalizeOrderSummary = (order: BackendOrderSummary): StoreOrderSummary => {
  // Some endpoints (e.g. admin orders list) return `status` itself as an
  // object like {status_name, label, key} instead of a scalar value.
  const statusObject = order.status && typeof order.status === 'object'
    ? order.status as Record<string, unknown>
    : null
  const statusValue = statusObject
    ? toText(statusObject.key) ?? toText(statusObject.status)
    : (typeof order.status === 'string' || typeof order.status === 'number' ? order.status : null)
  const statusObjectLabel = statusObject
    ? toText(statusObject.label) ?? toText(statusObject.status_name)
    : null

  return {
  id: toText(order.id) ?? '',
  reference_id: toText(order.reference_id) ?? '',
  status: statusValue,
  status_label: (Array.isArray(order.status_data)
    ? toText(order.status_data[0]?.more_info as string) ?? toText((order.status_data[0]?.more_info as Record<string, unknown> | undefined)?.status_name) ?? toText(order.status_data[0]?.status)
    : toText((order.status_data as BackendOrderStatusData)?.label) ?? statusObjectLabel ?? toText(statusValue)),
  is_terminal: Array.isArray(order.status_data)
    ? resolveTerminalFlag(order.status_data[0])
    : resolveTerminalFlag(order.status_data ?? statusObject),
  notes: toText(order.notes),
  type: order.type ?? null,
  type_label: toText(order.serve_type_label) ?? toText(order.type_str) ?? toText(order.order_type_str) ?? toText(order.type),
  serve_type: order.serve_type ?? null,
  serve_type_key: toText(order.serve_type_key),
  serve_type_label: toText(order.serve_type_label),
  source_type: order.source_type ?? null,
  source_type_str: toText(order.source_type_str),
  order_time: toText(order.order_time),
  delivery_time: toText(order.delivery_time),
  created_on: toText(order.created_on),
  is_scheduled: typeof order.is_scheduled === 'boolean' ? order.is_scheduled : null,
  is_paid: typeof order.is_paid === 'boolean' ? order.is_paid : null,
  payment_method: order.payment_method ?? null,
  payment_status: order.payment_status ?? null,
  payment_url: toText(order.payment_url),
  total: order.total ?? null,
  currency: toText(order.currency),
  customer_name: toText(order.user_data?.name|| order?.name),
  customer_id: order.user_data?.id ?? null,
  customer_email: toText(order.user_data?.email),
  customer_phone: toText(order.user_data?.phone) ?? toText(order.user_data?.mobile),
  address_label: toText(order.address_label) ?? getAddressLabel(order.delivery_address_data),
  order_item_count: Number(order.order_item_count ?? order.order_items_count ?? 0),
  linked_job_count: Number(order.linked_job_count ?? order.linked_jobs ?? 0),
  job_id: order.job_id ?? null,
  job_ref: toText(order.job_ref),
  service_instance_id: toText(order.service_instance_id),
  job_service_instance_id: toText(order.job_service_instance_id),
  jobs_summary: Array.isArray(order.jobs_summary) ? order.jobs_summary.map((entry) => ({
    id: entry.id ?? '',
    job_ref: toText(entry.job_ref),
    title: toText(entry.title),
    status: toText(entry.status),
    order_item_id: entry.order_item_id ?? null,
    scheduled_start: toText(entry.scheduled_start),
    assigned_worker_count: typeof entry.assigned_worker_count === 'number' ? entry.assigned_worker_count : 0,
    service_instance_id: toText(entry.service_instance_id),
    service_instance_pk: entry.service_instance_pk ?? null,
  })) : [],
  recurrence: normalizeOrderRecurrence(order.recurrence),
  }
}

const normalizeOrderRecurrence = (
  recurrence: BackendOrderSummary['recurrence'],
): OrderRecurrenceInfo | null => {
  if (!recurrence || !recurrence.rule) {
    return null
  }
  return {
    rule: recurrence.rule,
    interval_days: recurrence.interval_days ?? null,
    weekdays: Array.isArray(recurrence.weekdays)
      ? recurrence.weekdays.map((day) => Number(day)).filter((day) => Number.isInteger(day))
      : [],
    month_day: recurrence.month_day ?? null,
    end_date: toText(recurrence.end_date),
    occurrence_count: recurrence.occurrence_count ?? null,
    payment_plan: toText(recurrence.payment_plan),
    per_occurrence_total: toText(recurrence.per_occurrence_total),
    is_bounded: recurrence.is_bounded === true,
  }
}

const normalizeOrderItem = (item: NonNullable<BackendOrderDetail['order_item_data']>[number]): StoreOrderItem => {
  const productLevelPlannerLinks = normalizePlannerResourceLinks(item.product_data?.planner_resource_links)
  const addonLevelPlannerLinks = (item.order_addon_data ?? []).flatMap((addon) => normalizePlannerResourceLinks(addon.addon_data?.planner_resource_links))
  const addonVariantPlannerLinks = (item.order_addon_data ?? []).flatMap((addon) => normalizePlannerResourceLinks(addon.addon_product_variant_data?.planner_resource_links))
  const rawItem = item as Record<string, unknown>

  const encryptedOrTokenId =
    toText(rawItem.id) ??
    toText(rawItem.order_item) ??
    toText(rawItem.order_item_id)

  const maskedOrPlainId =
    (rawItem._id as string | number | null | undefined) ??
    (rawItem._order_item as string | number | null | undefined) ??
    (rawItem.order_item_id as string | number | null | undefined) ??
    null

  return {
  ...rawItem,
  id: encryptedOrTokenId,
  _id: maskedOrPlainId,
  included_features: Array.isArray(item.included_features) ? item.included_features : [],
  excluded_features: Array.isArray(item.excluded_features) ? item.excluded_features : [],
  product_name: toText(item.product_data?.name),
  product_type: item.product_data?.type ?? null,
  product_variant_name: toText(item.product_variant_data?.label) ?? toText(item.product_variant_data?.product_variant_name) ?? toText(item.product_variant_data?.product_variant_label),
  addon_names: (item.order_addon_data ?? [])
    .map((addon) => toText(addon.addon_data?.name))
    .filter((name): name is string => Boolean(name)),
  planner_resource_links: mergePlannerResourceLinks(
    productLevelPlannerLinks,
    normalizePlannerResourceLinks(item.product_variant_data?.planner_resource_links),
    addonLevelPlannerLinks,
    addonVariantPlannerLinks,
  ),
  qty: typeof item.qty === 'number' ? item.qty : null,
  unit_price: item.unit_price ?? null,
  final_price: item.final_price ?? null,
  location_text: toText(item.location_text ?? rawItem.location_text),
  latitude: (item.latitude ?? rawItem.latitude ?? null) as string | number | null,
  longitude: (item.longitude ?? rawItem.longitude ?? null) as string | number | null,
  applied_offer: rawItem.applied_offer as string | number | null | undefined ?? null,
  applied_offer_data: (rawItem.applied_offer_data as Record<string, unknown> | null | undefined) ?? null,
  job_id: (item.job_id ?? rawItem.job_id ?? null) as string | number | null,
  job_ref: toText(item.job_ref ?? rawItem.job_ref),
  job_status: toText(item.job_status ?? rawItem.job_status),
  job_service_instance_id: toText(item.job_service_instance_id ?? rawItem.job_service_instance_id),
  has_job: item.has_job === true || rawItem.has_job === true || item.job_id != null || rawItem.job_id != null,
  assigned_worker_count: typeof item.assigned_worker_count === 'number'
    ? item.assigned_worker_count
    : typeof rawItem.assigned_worker_count === 'number'
      ? rawItem.assigned_worker_count
      : null,
  }
}

export type PublicOrderPaymentDetail = {
  order: {
    id: string | number
    reference_id?: string | number | null
    customer_name?: string | null
    email?: string | null
    total?: string | number | null
    paid_amount?: string | number | null
    balance?: string | number | null
    is_paid?: boolean | null
    payment_status?: string | number | null
    currency?: string | null
    currency_code?: string | null
  }
  payment: {
    id: string | number
    amount?: string | number | null
    payment_reference_id?: string | null
    payment_status?: string | number | null
    payment_gateway?: string | number | null
    payment_gateway_name?: string | null
    payment_link?: string | null
    publishable_key?: string | null
    signature?: string | null
  }
  paid?: boolean
}
const normalizeTotalLines = (lines?: BackendOrderDetail['total']): StoreOrderTotalLine[] => (
  Array.isArray(lines)
    ? lines
      .filter((line): line is NonNullable<BackendOrderDetail['total']>[number] => Boolean(line?.label))
      .map((line) => ({
        label: line.label ?? '',
        amount: line.amount ?? null,
        currency: toText(line.currency),
        sign: toText(line.sign),
        value: line.value ?? null,
      }))
    : []
)

const resolveOrderItems = (order: BackendOrderDetail): NonNullable<BackendOrderDetail['order_item_data']> => {
  if (Array.isArray(order.order_item_data)) {
    return order.order_item_data
  }
  if (Array.isArray(order.order_items_data)) {
    return order.order_items_data
  }
  return []
}

const normalizeOrderDetail = (order: BackendOrderDetail): StoreOrderDetail => ({
  ...normalizeOrderSummary(order),
  _id: order._id ?? null,
  raw_id: unmaskMaskedId(order._id),
  service_instance: order.service_instance ?? null,
  service_instance_id: toText(order.service_instance_id),
  service_instance_name: toText(order.service_instance_data?.name),
  full_name: toText(order.full_name),
  email: toText(order.email),
  mobile: toText(order.mobile),
  payment_status: order.payment_status ?? null,
  payment_mode_name: toText(order.payment_mode_name),
  final_total: order.final_total ?? null,
  planner_resource_links: normalizePlannerResourceLinks(order.planner_resource_links),
  order_items: resolveOrderItems(order).map(normalizeOrderItem),
  totals: normalizeTotalLines(order.total),
  status_history: Array.isArray(order.status_data) ? order.status_data : [],
  payment_data: Array.isArray(order.payment_data) ? order.payment_data : [],
  offers_applied: Array.isArray(order.offers_applied) ? order.offers_applied : [],
  delivery_address_data: order.delivery_address_data ?? null,
  user_data: order.user_data ?? null,
  job_id: order.job_id ?? null,
  has_job: order.has_job === true,
  job_ref: toText(order.job_ref),
  job_scope: toText(order.job_scope),
  job_ids: Array.isArray(order.job_ids) ? order.job_ids : [],
  job_service_instance_id: toText(order.job_service_instance_id),
  jobs_summary: Array.isArray(order.jobs_summary) ? order.jobs_summary.map((entry) => ({
    id: entry.id ?? '',
    job_ref: toText(entry.job_ref),
    title: toText(entry.title),
    status: toText(entry.status),
    order_item_id: entry.order_item_id ?? null,
    scheduled_start: toText(entry.scheduled_start),
    assigned_worker_count: typeof entry.assigned_worker_count === 'number' ? entry.assigned_worker_count : 0,
    service_instance_id: toText(entry.service_instance_id),
    service_instance_pk: entry.service_instance_pk ?? null,
  })) : [],
})

const normalizeOrderPayment = (payment: BackendOrderPayment): StoreOrderPayment => ({
  id: payment.id ?? payment._id ?? '',
  _id: payment._id ?? null,
  amount: payment.amount ?? null,
  currency: toText(payment.currency),
  payment_reference_id: toText(payment.payment_reference_id),
  payment_reference_number: toText(payment.payment_reference_number),
  order_reference_id: toText(payment.order_reference_id),
  customer_name: toText(payment.customer_name),
  customer_phone: toText(payment.customer_phone),
  customer_email: toText(payment.customer_email),
  payment_service: payment.payment_service ?? null,
  payment_method: payment.payment_method ?? null,
  payment_mode: payment.payment_mode ?? null,
  payment_status: payment.payment_status ?? null,
  created_on: toText(payment.created_on),
  updated_on: toText(payment.updated_on),
})

const normalizeOrderRefund = (refund: BackendOrderRefund): StoreOrderRefund => ({
  id: refund.id ?? refund._id ?? '',
  _id: refund._id ?? null,
  order: refund.order ?? null,
  order_id: refund.order_id ?? null,
  order_reference_id: refund.order_reference_id ?? null,
  order_total_amount: refund.order_total_amount ?? null,
  payment_transaction: refund.payment_transaction ?? null,
  amount: refund.amount ?? null,
  gross_amount: refund.gross_amount ?? null,
  deduction_amount: refund.deduction_amount ?? null,
  currency: toText(refund.currency),
  status: refund.status ?? null,
  source: toText(refund.source),
  settlement_type: toText(refund.settlement_type),
  refund_wallet_id: refund.refund_wallet_id ?? null,
  refund_wallet_label: toText(refund.refund_wallet_label),
  applied_rule_id: refund.applied_rule_id ?? null,
  refund_rule_title: toText(refund.refund_rule_title),
  gateway_refund_id: toText(refund.gateway_refund_id),
  reason: toText(refund.reason),
  rule_snapshot: refund.rule_snapshot ?? null,
  payment_method_label: toText(refund.payment_method_label),
  payment_reference_id: toText(refund.payment_reference_id),
  gateway_payment_reference_id: toText(refund.gateway_payment_reference_id),
  paid_status: toText(refund.paid_status),
  paid_on: toText(refund.paid_on),
  auto_refund_eligible: typeof refund.auto_refund_eligible === 'boolean' ? refund.auto_refund_eligible : null,
  auto_refund_reason: toText(refund.auto_refund_reason),
  initiated_by: refund.initiated_by
    ? {
      id: refund.initiated_by.id ?? null,
      name: toText(refund.initiated_by.name),
    }
    : null,
  reviewed_by: refund.reviewed_by
    ? {
      id: refund.reviewed_by.id ?? null,
      name: toText(refund.reviewed_by.name),
    }
    : null,
  created_on: toText(refund.created_on),
  updated_on: toText(refund.updated_on),
})

const normalizeOrderItemMedia = (media: BackendOrderItemMedia): StoreOrderItemMedia => normalizeOrderItemMediaRecord({
  id: toText(media.id) ?? '',
  _id: media._id ?? null,
  order_item: toText(media.order_item),
  _order_item: media._order_item ?? null,
  basket_item: toText(media.basket_item),
  _basket_item: media._basket_item ?? null,
  mime: toText(media.mime),
  path: toText(media.path),
  file_size: typeof media.file_size === 'number' ? media.file_size : null,
  type: media.type ?? null,
  type_str: toText(media.type_str),
  created_on: toText(media.created_on),
  updated_on: toText(media.updated_on),
})

export const ordersApi = {
  listOrders: async (params?: OrdersQueryParams): Promise<OrdersListResponse> => {
    const response = await api.get<ApiResponse<OrdersListResponse> | OrdersListResponse>(
      API_PATHS.orders.list,
      { params },
    )
    const payload = 'data' in response.data ? response.data.data : response.data

    return {
      ...payload,
      results: payload.results.map(normalizeOrderSummary),
    }
  },

  getOrderStats: async (params?: Omit<OrdersQueryParams, 'page' | 'page_size'>): Promise<OrdersStatsResponse> => {
    const response = await api.get<ApiResponse<OrdersStatsResponse> | OrdersStatsResponse>(
      API_PATHS.orders.stats,
      { params },
    )

    return 'data' in response.data ? response.data.data : response.data
  },

  getOrderDetail: async (referenceId: string): Promise<StoreOrderDetail> => {
    const response = await api.get<ApiResponse<BackendOrderDetail>>(API_PATHS.orders.detail(referenceId))
    return normalizeOrderDetail(response.data.data)
  },

  getOrderConfirmationDetail: async (referenceId: string): Promise<StoreOrderDetail> => {
    const response = await api.get<ApiResponse<BackendOrderDetail> | BackendOrderDetail>(
      API_PATHS.orders.detail(referenceId),
    )
    const payload = 'data' in response.data ? response.data.data : response.data
    return normalizeOrderDetail(payload)
  },

  getPublicOrderPaymentDetail: async (
    orderId: string | number,
    paymentId: string | number,
    token: string,
  ): Promise<PublicOrderPaymentDetail> => {
    const response = await api.get<PublicOrderPaymentDetail>(API_PATHS.orders.publicPaymentDetail(orderId, paymentId), {
      params: { token },
    })
    return response.data
  },

  getPublicOrderPaymentDetailByToken: async (token: string): Promise<PublicOrderPaymentDetail> => {
    const response = await api.get<PublicOrderPaymentDetail>(API_PATHS.orders.publicPaymentDetailByToken(token))
    return response.data
  },

  generateOrderPaymentLink: async (orderId: string | number): Promise<{ payment_url: string }> => {
    const response = await api.post<ApiResponse<{ payment_url: string }>>(
      API_PATHS.orders.generatePaymentLink(orderId),
      undefined,
      { params: { platform: 5 } },
    )
    return response.data.data
  },

  listOrderPayments: async (orderId: string | number): Promise<PaginatedResponse<StoreOrderPayment>> => {
    const response = await api.get<ApiResponse<PaginatedResponse<BackendOrderPayment>> | PaginatedResponse<BackendOrderPayment>>(
      API_PATHS.orders.payments,
      { params: { order: orderId, page_size: 100 } },
    )
    const payload = 'data' in response.data ? response.data.data : response.data

    return {
      ...payload,
      results: payload.results.map(normalizeOrderPayment),
    }
  },

  listOrderPaymentsDirectory: async (params?: {
    page?: number
    page_size?: number
    service_instance_id?: string | number
    search?: string
  }): Promise<PaginatedResponse<StoreOrderPayment>> => {
    const queryParams: Record<string, string | number> = {}
    if (typeof params?.page === 'number' && Number.isFinite(params.page) && params.page > 0) {
      queryParams.page = params.page
    }
    if (typeof params?.page_size === 'number' && Number.isFinite(params.page_size) && params.page_size > 0) {
      queryParams.page_size = params.page_size
    }

    const serviceInstanceId = String(params?.service_instance_id ?? '').trim()
    if (serviceInstanceId) {
      queryParams.service_instance_id = serviceInstanceId
    }

    const searchText = String(params?.search ?? '').trim()
    if (searchText) {
      queryParams.search = searchText
    }

    const response = await api.get<ApiResponse<PaginatedResponse<BackendOrderPayment>> | PaginatedResponse<BackendOrderPayment>>(
      API_PATHS.orders.payments,
      { params: queryParams },
    )
    const payload = 'data' in response.data ? response.data.data : response.data

    return {
      ...payload,
      results: payload.results.map(normalizeOrderPayment),
    }
  },

  createOrderPayment: async (payload: CreateOrderPaymentPayload): Promise<StoreOrderPayment> => {
    const response = await api.post<ApiResponse<BackendOrderPayment>>(API_PATHS.orders.createPayment, payload)
    return normalizeOrderPayment(response.data.data)
  },

  deleteOrderPayment: async (paymentId: string | number): Promise<void> => {
    await api.delete(API_PATHS.orders.removePayment(paymentId))
  },

  approveOrderRefund: async (refundId: string | number): Promise<StoreOrderRefund> => {
    const response = await api.post<ApiResponse<{ refund?: BackendOrderRefund } | BackendOrderRefund>>(
      API_PATHS.orders.approveRefund(refundId),
    )
    const payload = response.data.data
    const refund = (payload as { refund?: BackendOrderRefund })?.refund ?? (payload as BackendOrderRefund)
    return normalizeOrderRefund(refund)
  },

  rejectOrderRefund: async (refundId: string | number, reason?: string): Promise<StoreOrderRefund> => {
    const response = await api.post<ApiResponse<{ refund?: BackendOrderRefund } | BackendOrderRefund>>(
      API_PATHS.orders.rejectRefund(refundId),
      reason?.trim() ? { reason: reason.trim() } : undefined,
    )
    const payload = response.data.data
    const refund = (payload as { refund?: BackendOrderRefund })?.refund ?? (payload as BackendOrderRefund)
    return normalizeOrderRefund(refund)
  },

  deleteOrderRefund: async (refundId: string | number): Promise<void> => {
    await api.delete(API_PATHS.orders.removeRefund(refundId))
  },

  listRefundDirectory: async (params?: {
    page?: number
    page_size?: number
    service_instance_id?: string | number
    search?: string
    status?: string
  }): Promise<PaginatedResponse<StoreOrderRefund>> => {
    const queryParams: Record<string, string | number> = {}
    if (typeof params?.page === 'number' && Number.isFinite(params.page) && params.page > 0) {
      queryParams.page = params.page
    }
    if (typeof params?.page_size === 'number' && Number.isFinite(params.page_size) && params.page_size > 0) {
      queryParams.page_size = params.page_size
    }

    const serviceInstanceId = String(params?.service_instance_id ?? '').trim()
    if (serviceInstanceId) {
      queryParams.service_instance_id = serviceInstanceId
    }

    const searchText = String(params?.search ?? '').trim()
    if (searchText) {
      queryParams.search = searchText
    }

    const statusText = String(params?.status ?? '').trim()
    if (statusText) {
      queryParams.status = statusText
    }

    const response = await api.get<ApiResponse<PaginatedResponse<BackendOrderRefund>> | PaginatedResponse<BackendOrderRefund>>(
      API_PATHS.orders.refunds,
      { params: queryParams },
    )
    const payload = 'data' in response.data ? response.data.data : response.data

    return {
      ...payload,
      results: payload.results.map(normalizeOrderRefund),
    }
  },

  listOrderItemMedia: async (params: {
    orderItemId?: string | number | null
    basketItemId?: string | number | null
    serviceInstanceId?: string | number | null
  }): Promise<StoreOrderItemMedia[]> => {
    const queryParams: Record<string, string | number> = {}
    if (params.orderItemId != null && String(params.orderItemId).trim().length > 0) {
      queryParams.order_item = params.orderItemId
    } else if (params.basketItemId != null && String(params.basketItemId).trim().length > 0) {
      queryParams.basket_item = params.basketItemId
    }
    if (params.serviceInstanceId != null && String(params.serviceInstanceId).trim().length > 0) {
      queryParams.service_instance_id = params.serviceInstanceId
    }

    const response = await api.get<ApiResponse<BackendOrderItemMedia[]> | BackendOrderItemMedia[]>(
      API_PATHS.orders.orderItemMediaList,
      { params: queryParams },
    )
    const payload = 'data' in response.data ? response.data.data : response.data
    return (Array.isArray(payload) ? payload : []).map(normalizeOrderItemMedia)
  },

  createOrderItemMedia: async (params: {
    orderItemId?: string | number | null
    basketItemId?: string | number | null
    serviceInstanceId?: string | number | null
  }, files: File[]): Promise<StoreOrderItemMedia[]> => {
    const formData = new FormData()
    if (params.orderItemId != null && String(params.orderItemId).trim().length > 0) {
      formData.append('order_item', String(params.orderItemId))
    } else if (params.basketItemId != null && String(params.basketItemId).trim().length > 0) {
      formData.append('basket_item', String(params.basketItemId))
    }
    if (params.serviceInstanceId != null && String(params.serviceInstanceId).trim().length > 0) {
      formData.append('service_instance_id', String(params.serviceInstanceId))
    }

    for (const file of files) {
      formData.append('file', file)
    }

    const response = await api.post<ApiResponse<BackendOrderItemMedia[]>>(
      API_PATHS.orders.orderItemMediaCreate,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )

    return (Array.isArray(response.data.data) ? response.data.data : []).map(normalizeOrderItemMedia)
  },

  deleteOrderItemMedia: async (mediaId: string | number): Promise<void> => {
    await api.delete(API_PATHS.orders.orderItemMediaDelete(mediaId))
  },

  resendConfirmationEmail: async (payload: ResendOrderConfirmationPayload): Promise<{ message?: string }> => {
    const response = await api.post<{ message?: string; data?: { message?: string }; status?: string }>(
      API_PATHS.orders.resendConfirmationEmail,
      payload,
    )
    const body = response.data
    if (body?.data && typeof body.data === 'object' && 'message' in body.data) {
      return body.data
    }
    return { message: body?.message }
  },
}

export default ordersApi